import {
  Css,
  InlineFeedbackBanner,
  InlineFeedbackBannerAction,
  InlineFeedbackBannerProps,
  InlineFeedbackBannerType,
} from "@homebound/beam";
import { CostCodeBidSummaryFlag, CostType } from "src/generated/graphqlTypes";
import { foldEnum } from "src/utils";
import {
  bidPackageCreateOptions,
  bidPackageEditHref,
  bidPackageHref,
  CostCodeBidSummaryGroup,
  GroupFlag,
  lastAwardedBidPackage,
  productOfferingsNotCovered,
} from "./utils";

export type FlagBannersProps = { group: CostCodeBidSummaryGroup; projectId: string };

/** One banner per flag of the group, most severe first, as the summary row's companion. */
export function CostCodeBidSummaryBanners(props: FlagBannersProps) {
  return (
    <div css={Css.df.fdc.gap1.$}>
      {bannerFlags(props.group.flags).map((flag) => (
        <InlineFeedbackBanner key={flag} {...flagBannerProps(flag, props)} />
      ))}
    </div>
  );
}

export function flagBannerProps(flag: GroupFlag, props: FlagBannersProps): InlineFeedbackBannerProps {
  const { group } = props;
  const { type, tagText, description, actions } = flagCopy[flag];
  return {
    type,
    tagText,
    description: description(groupScopeName(group), group.uncovered),
    actions: bannerActions(actions, props),
  };
}

type FlagCopy = {
  type: InlineFeedbackBannerType;
  tagText: string;
  description: (scope: string, uncovered: CostCodeBidSummaryGroup["uncovered"]) => string;
  actions: "create" | "editAndCreate" | "view";
};

// Listed most severe first: `bannerFlags` stacks the banners in this order.
// Backend flags carry no counts, so the copy stays generic except for the product offerings the FE counts itself.
// TODO: update to showing counts when it's supported on the BE.
const flagCopy: Record<GroupFlag, FlagCopy> = {
  [productOfferingsNotCovered]: {
    type: "error",
    tagText: "PRODUCT OFFERINGS NOT COVERED",
    description: (scope, { missing, total }) => `${missing}/${total} Product Offerings are missing ${scope} costs.`,
    actions: "editAndCreate",
  },
  [CostCodeBidSummaryFlag.MissingBid]: {
    type: "error",
    tagText: "MISSING BIDS",
    description: (scope) => `Scope was found for ${scope} but no bids exist.`,
    actions: "create",
  },
  [CostCodeBidSummaryFlag.MissingRequiredBid]: {
    type: "error",
    tagText: "MISSING REQUIRED BID",
    description: (scope) =>
      `No scope has been entered for this cost code, but ${scope} is categorized as always expected in Homebound's bidding.`,
    actions: "create",
  },
  [CostCodeBidSummaryFlag.MissingCosts]: {
    type: "warning",
    tagText: "MISSING COSTS",
    description: () => "Some bid packages are missing costs.",
    actions: "view",
  },
  [CostCodeBidSummaryFlag.MissingOptions]: {
    type: "warning",
    tagText: "MISSING OPTIONS",
    description: () => "Options are missing from the awarded bid package.",
    actions: "view",
  },
  [CostCodeBidSummaryFlag.ExcludedMaterial]: {
    type: "warning",
    tagText: "EXCLUDED MATERIAL",
    description: () => "A material was excluded from the awarded bid package.",
    actions: "view",
  },
  [CostCodeBidSummaryFlag.ExcludedTasks]: {
    type: "warning",
    tagText: "EXCLUDED TASKS",
    description: () => "A task was excluded from the awarded bid package.",
    actions: "view",
  },
  [CostCodeBidSummaryFlag.CostMissingScope]: {
    type: "warning",
    tagText: "COST MISSING SCOPE",
    description: () =>
      "Costs have been awarded but do not have any associated takeoff line items. Remove the bid line or contact the estimation team to update takeoffs.",
    actions: "view",
  },
  [CostCodeBidSummaryFlag.MissingTrade]: {
    type: "warning",
    tagText: "MISSING TRADE",
    description: () => "This bid is a provisional estimate and needs to be awarded to a trade.",
    actions: "view",
  },
};

const flagOrder = Object.keys(flagCopy) as GroupFlag[];

/** A group's flags in the order their banners stack, i.e. most severe first. */
export function bannerFlags(flags: GroupFlag[]): GroupFlag[] {
  return flags.sortBy((f) => flagOrder.indexOf(f));
}

/** How the banners name the group (i.e. "Framing Labor"), a subcontractor bid covers the whole cost code. */
function groupScopeName({ costCode, costType }: Pick<CostCodeBidSummaryGroup, "costCode" | "costType">) {
  return costType.code === CostType.Subcontractor ? costCode.name : `${costCode.name} ${costType.name}`;
}

/** Missing bids link to creating a package, the other flags link to the last awarded package. */
function bannerActions(kind: FlagCopy["actions"], props: FlagBannersProps): InlineFeedbackBannerAction[] {
  const { group, projectId } = props;
  const target = lastAwardedBidPackage(group.bidPackages);
  // Prefill the create stepper with this group's cost code + cost type.
  const createOpts = { costCodes: [group.costCode.id], costType: group.costType.code };
  // A package is either plan based or unit based, so creating asks which, the same menu as the page header's.
  const create = {
    kind: "menu" as const,
    label: "Create Bid Package",
    items: bidPackageCreateOptions(projectId, createOpts).map(({ label, url }) => ({ label, onClick: url })),
  };
  return foldEnum(kind, {
    create: () => [create],
    editAndCreate: () =>
      [target && { label: "Edit Bid Package", onClick: bidPackageEditHref(target, projectId) }, create].compact(),
    view: () => (target ? [{ label: "View", onClick: bidPackageHref(target, projectId) }] : []),
  });
}
